import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../api/axios";

/* ── COVER GRADIENTS ── */
const GRADIENTS = {
  "gradient-1": "linear-gradient(135deg, #2e1a0e 0%, #8b4e2a 50%, #c09070 100%)",
  "gradient-2": "linear-gradient(135deg, #0a1628 0%, #1a3a6b 50%, #4a7fc1 100%)",
  "gradient-3": "linear-gradient(135deg, #0d2818 0%, #1a6b3a 50%, #4ac17f 100%)",
  "gradient-4": "linear-gradient(135deg, #1a0a28 0%, #5a1a8b 50%, #b04ac1 100%)",
  "gradient-5": "linear-gradient(135deg, #281a0a 0%, #8b6a1a 50%, #c1a84a 100%)",
  "gradient-6": "linear-gradient(135deg, #0a2020 0%, #1a6b6b 50%, #4ac1c1 100%)",
};

const GRADIENT_NAMES = {
  "gradient-1": "Ember",
  "gradient-2": "Ocean",
  "gradient-3": "Forest",
  "gradient-4": "Galaxy",
  "gradient-5": "Gold",
  "gradient-6": "Teal",
};

/* ── LETTER AVATAR COLORS ── */
const AVATAR_COLORS = [
  ["#8b4e2a","#f4ece4"], ["#1a3a6b","#e8f0fe"], ["#1a6b3a","#e6fcf5"],
  ["#5a1a8b","#f3e8ff"], ["#8b6a1a","#fff8e6"], ["#1a6b6b","#e8fffe"],
];
const getAvatarColor = (name="") => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const S = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

.vp-page{min-height:100vh;background:var(--surface-2);font-family:var(--font-body)}

/* TOPBAR */
.vp-topbar{background:var(--surface);padding:0 40px;height:58px;display:flex;justify-content:space-between;align-items:center;position:sticky;top:0;z-index:100}
.vp-logo{font-family:var(--font-display);font-size:20px;color:var(--text);cursor:pointer;letter-spacing:.3px}
.vp-back{padding:8px 18px;background:transparent;border:1.5px solid var(--border-strong);border-radius:10px;color:var(--accent);font-family:var(--font-body);font-size:13px;font-weight:600;cursor:pointer;transition:all .18s}
.vp-back:hover{background:var(--accent-tint);border-color:var(--accent);color:var(--text)}

/* HERO */
.vp-hero{position:relative;height:220px;overflow:hidden}
.vp-cover{width:100%;height:100%;object-fit:cover}
.vp-cover-overlay{position:absolute;inset:0;background:rgba(0,0,0,.25)}
.vp-cover-edit{position:absolute;top:14px;right:14px;padding:7px 14px;background:var(--overlay);backdrop-filter:blur(8px);border:1px solid var(--border);border-radius:8px;color:white;font-family:var(--font-body);font-size:12px;font-weight:600;cursor:pointer;transition:all .18s;display:flex;align-items:center;gap:6px}
.vp-cover-edit:hover{background:var(--overlay)}

/* AVATAR AREA */
.vp-avatar-area{position:relative;margin-top:-60px;padding:0 40px;display:flex;align-items:flex-end;gap:20px}
.vp-avatar-wrap{position:relative;flex-shrink:0}
.vp-avatar{width:110px;height:110px;border-radius:50%;border:4px solid var(--surface);object-fit:cover;display:flex;align-items:center;justify-content:center;font-family:var(--font-display);font-size:44px;font-weight:700;overflow:hidden}
.vp-avatar-img{width:100%;height:100%;object-fit:cover;border-radius:50%}
.vp-avatar-edit-btn{position:absolute;bottom:4px;right:4px;width:30px;height:30px;background:var(--surface);border:2px solid var(--surface);border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:13px;transition:background .18s}
.vp-avatar-edit-btn:hover{background:var(--accent-hover)}
.vp-avatar-uploading{position:absolute;inset:0;border-radius:50%;background:var(--overlay);display:flex;align-items:center;justify-content:center;color:white;font-size:11px;font-weight:600}
.vp-identity{align-self:flex-start;margin-top:72px;flex:1;min-width:0}
.vp-identity-name{font-family:var(--font-display);font-size:28px;font-weight:500;letter-spacing:-.02em;color:var(--text);margin-bottom:var(--space-1)}
.vp-identity-email{font-size:13px;color:var(--text-3);margin-bottom:6px}
.vp-identity-badges{display:flex;gap:8px;flex-wrap:wrap}
.vp-role-badge{padding:3px 12px;background:var(--accent);color:var(--on-accent);border-radius:20px;font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase}
.vp-member-badge{padding:3px 12px;background:var(--surface-2);color:var(--text-2);border-radius:20px;font-size:11px;font-weight:600}

/* BODY */
.vp-body{max-width:1000px;margin:0 auto;padding:28px 40px 60px;display:grid;grid-template-columns:1fr 340px;gap:22px}
@media(max-width:820px){.vp-body{grid-template-columns:1fr;padding:20px}}

/* CARDS */
.vp-card{background:var(--surface);border-radius:20px;padding:26px;border:1px solid var(--border);margin-bottom:18px}
.vp-card-title{font-family:var(--font-display);font-size:19px;color:var(--text);margin-bottom:18px;display:flex;align-items:center;gap:12px}
.vp-card-line{flex:1;height:1px;background:linear-gradient(90deg,var(--border),transparent)}

/* FORM */
.vp-label{font-size:11px;font-weight:700;color:var(--text-3);text-transform:uppercase;letter-spacing:.7px;display:block;margin:12px 0 5px}
.vp-input{width:100%;padding:12px 16px;background:var(--surface);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:14px;color:var(--text);outline:none;transition:all .2s;margin-bottom:4px}
.vp-input::placeholder{color:var(--text-3)}
.vp-input:focus{border-color:var(--accent);box-shadow:var(--ring)}
.vp-input:disabled{background:var(--surface-2);color:var(--text-3);box-shadow:none;cursor:not-allowed}
.vp-textarea{width:100%;padding:12px 16px;background:var(--surface);border:1px solid var(--border-strong);border-radius:var(--radius);box-shadow:var(--shadow-xs);font-family:var(--font-body);font-size:14px;color:var(--text);outline:none;resize:none;transition:all .2s;min-height:80px}
.vp-textarea::placeholder{color:var(--text-3)}
.vp-textarea:focus{border-color:var(--accent);box-shadow:var(--ring)}
.vp-char-count{font-size:11px;color:var(--text-3);text-align:right;margin-top:3px}
.vp-hint{font-size:12px;color:var(--accent);margin:8px 0 12px;line-height:1.6;padding:10px 14px;border:1px solid var(--border);border-radius:10px;background:var(--accent-tint)}
.vp-input-icon-row{position:relative}
.vp-input-icon{position:absolute;left:14px;top:50%;transform:translateY(-50%);font-size:15px;pointer-events:none}
.vp-input-with-icon{padding-left:40px}

/* BUTTONS */
.vp-btn-primary{padding:11px 24px;background:var(--accent);border:none;border-radius:12px;color:var(--on-accent);font-family:var(--font-body);font-size:13px;font-weight:700;cursor:pointer;transition:all .2s;display:inline-flex;align-items:center;gap:8px}
.vp-btn-primary:hover:not(:disabled){background:var(--accent-hover);transform:translateY(-1px);box-shadow:var(--shadow-md)}
.vp-btn-primary:disabled{opacity:.5;cursor:not-allowed;transform:none}
.vp-btn-ghost{padding:11px 22px;background:transparent;border:1.5px solid var(--border-strong);border-radius:12px;color:var(--text-2);font-family:var(--font-body);font-size:13px;font-weight:500;cursor:pointer;transition:all .2s}
.vp-btn-ghost:hover{border-color:var(--accent);color:var(--text)}
.vp-btn-danger{padding:11px 22px;background:var(--danger-tint);border:1.5px solid var(--danger-border);border-radius:12px;color:var(--danger);font-family:var(--font-body);font-size:13px;font-weight:600;cursor:pointer;transition:all .2s}
.vp-btn-danger:hover{background:var(--danger-tint)}
.vp-btn-row{display:flex;gap:10px;margin-top:16px;flex-wrap:wrap}

/* STATS GRID */
.vp-stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:4px}
.vp-stat{background:var(--surface);border-radius:16px;padding:18px;border:1px solid var(--border);text-align:center}
.vp-stat-num{font-family:var(--font-display);font-size:32px;color:var(--accent);line-height:1}
.vp-stat-label{font-size:12px;color:var(--text-3);margin-top:4px;font-weight:500}

/* COVER PICKER */
.vp-cover-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:4px}
.vp-cover-swatch{height:48px;border-radius:10px;cursor:pointer;border:2px solid transparent;transition:all .18s;position:relative;overflow:hidden}
.vp-cover-swatch:hover{transform:scale(1.04)}
.vp-cover-swatch.active{border-color:var(--border-strong);box-shadow:0 0 0 2px var(--accent)}
.vp-cover-swatch-label{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:var(--text);text-shadow:var(--shadow-sm)}

/* BADGES */
.vp-badges-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.vp-badge{background:var(--surface);border:1px solid var(--border);border-radius:14px;padding:14px;display:flex;align-items:center;gap:10px}
.vp-badge-icon{font-size:24px;flex-shrink:0}
.vp-badge-name{font-size:13px;font-weight:700;color:var(--text)}
.vp-badge-desc{font-size:11px;color:var(--text-3);margin-top:2px}
.vp-badge-locked{opacity:.35;filter:grayscale(1)}

/* ACTIVITY */
.vp-activity-item{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--border)}
.vp-activity-item:last-child{border-bottom:none}
.vp-activity-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.vp-activity-dot.created{background:var(--success)}
.vp-activity-dot.updated{background:var(--accent)}
.vp-activity-dot.deleted{background:var(--danger)}
.vp-activity-title{font-size:13px;color:var(--text);font-weight:500;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vp-activity-date{font-size:11px;color:var(--text-3);flex-shrink:0}

/* SECURITY */
.vp-security-row{display:flex;justify-content:space-between;align-items:center;padding:12px 0;border-bottom:1px solid var(--border)}
.vp-security-row:last-child{border-bottom:none}
.vp-security-label{font-size:14px;color:var(--text);font-weight:500}
.vp-security-value{font-size:13px;color:var(--text-3)}

/* CROP MODAL */
.vp-modal-overlay{position:fixed;inset:0;background:var(--overlay);backdrop-filter:blur(6px);z-index:400;display:flex;align-items:center;justify-content:center;padding:20px}
.vp-modal{background:var(--bg);border-radius:22px;padding:32px;width:100%;max-width:440px;box-shadow:var(--shadow-lg);animation:vpModalIn .22s ease}
@keyframes vpModalIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}}
.vp-modal-title{font-family:var(--font-display);font-size:22px;color:var(--text);margin-bottom:20px}
.vp-crop-preview{width:160px;height:160px;border-radius:50%;margin:0 auto 20px;overflow:hidden;border:3px solid var(--accent);background:var(--surface-2);display:flex;align-items:center;justify-content:center}
.vp-crop-preview img{width:100%;height:100%;object-fit:cover}
.vp-modal-btn-row{display:flex;gap:10px;margin-top:20px}

@keyframes vsFadeIn{from{opacity:0}to{opacity:1}}
`;

function Profile() {
  const [user, setUser]                 = useState(null);
  const [stats, setStats]               = useState(null);

  // Form state
  const [name, setName]                 = useState("");
  const [bio, setBio]                   = useState("");
  const [github, setGithub]             = useState("");
  const [linkedin, setLinkedin]         = useState("");
  const [portfolio, setPortfolio]       = useState("");
  const [coverGradient, setCoverGradient] = useState("gradient-1");
  const [currentPassword, setCurrentPw] = useState("");
  const [newPassword, setNewPw]         = useState("");
  const [confirmPassword, setConfirmPw] = useState("");

  // Loading states
  const [profileLoading, setProfileLoading] = useState(false);
  const [pwLoading, setPwLoading]         = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Avatar crop modal
  const [showCropModal, setShowCropModal] = useState(false);
  const [previewSrc, setPreviewSrc]       = useState(null);
  const [pendingFile, setPendingFile]     = useState(null);

  const avatarInputRef = useRef(null);
  const navigate       = useNavigate();

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = S;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  useEffect(() => { fetchProfile(); fetchStats(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await API.get("/users/profile");
      const u = res.data.user;
      setUser(u);
      setName(u.name || "");
      setBio(u.bio || "");
      setGithub(u.github || "");
      setLinkedin(u.linkedin || "");
      setPortfolio(u.portfolio || "");
      setCoverGradient(u.coverGradient || "gradient-1");
    } catch { toast.error("Failed to load profile"); }
  };

  const fetchStats = async () => {
    try {
      const res = await API.get("/users/stats");
      setStats(res.data);
    } catch { /* stats optional */ }
  };

  /* ── AVATAR PICK ── */
  const handleAvatarPick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    const url = URL.createObjectURL(file);
    setPreviewSrc(url);
    setPendingFile(file);
    setShowCropModal(true);
  };

  /* ── AVATAR UPLOAD ── */
  const confirmAvatarUpload = async () => {
    if (!pendingFile) return;
    setShowCropModal(false);
    setAvatarUploading(true);
    const fd = new FormData();
    fd.append("avatar", pendingFile);
    try {
      const res = await API.post("/users/avatar", fd);
      toast.success("Profile picture updated! 🎉");
      setUser(prev => ({ ...prev, avatar: res.data.avatar }));
      // sync localStorage
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored) { stored.avatar = res.data.avatar; localStorage.setItem("user", JSON.stringify(stored)); }
    } catch { toast.error("Upload failed"); }
    finally { setAvatarUploading(false); setPendingFile(null); URL.revokeObjectURL(previewSrc); }
  };

  /* ── REMOVE AVATAR ── */
  const removeAvatar = async () => {
    if (!window.confirm("Remove profile picture?")) return;
    try {
      await API.delete("/users/avatar");
      toast.success("Avatar removed");
      setUser(prev => ({ ...prev, avatar: { url: null, publicId: null } }));
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored) { stored.avatar = null; localStorage.setItem("user", JSON.stringify(stored)); }
    } catch { toast.error("Failed to remove avatar"); }
  };

  /* ── SAVE PROFILE ── */
  const saveProfile = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setProfileLoading(true);
    try {
      const res = await API.put("/users/profile", { name, bio, github, linkedin, portfolio, coverGradient });
      toast.success("Profile saved ✅");
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored) { Object.assign(stored, res.data.user); localStorage.setItem("user", JSON.stringify(stored)); }
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.message || "Save failed"); }
    finally { setProfileLoading(false); }
  };

  /* ── CHANGE PASSWORD ── */
  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) { toast.error("Fill in all password fields"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setPwLoading(true);
    try {
      await API.put("/users/profile", { currentPassword, newPassword });
      toast.success("Password changed ✅");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
    finally { setPwLoading(false); }
  };

  const logout = () => {
    if (!window.confirm("Sign out?")) return;
    localStorage.clear(); window.location.href = "/";
  };

  if (!user) return (
    <div className="vp-page" style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh"}}>
      <p style={{color:"var(--text-3)",fontFamily:"var(--font-body)"}}>Loading profile…</p>
    </div>
  );

  const avatarColors = getAvatarColor(user.name);
  const initials = user.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2) || "?";
  const daysOnPlatform = stats?.daysOnPlatform || 0;
  const ALL_BADGES = [
    {id:"first",    icon:"🚀",label:"First Project",desc:"Created first project"},
    {id:"builder",  icon:"🏗️",label:"Builder",      desc:"5+ projects created"},
    {id:"poweruser",icon:"⚡",label:"Power User",   desc:"10+ projects"},
    {id:"uploader", icon:"📁",label:"File Master",  desc:"3+ files uploaded"},
    {id:"week",     icon:"🗓️",label:"One Week In",  desc:"7+ days active"},
    {id:"month",    icon:"🏅",label:"Veteran",      desc:"30+ days active"},
  ];
  const earnedIds = new Set((stats?.badges||[]).map(b=>b.id));

  return (
    <div className="vp-page">
      {/* TOPBAR */}
      <div className="vp-topbar">
        <span className="vp-logo" onClick={()=>navigate("/dashboard")}>VaultSphere</span>
        <button className="vp-back" onClick={()=>navigate("/dashboard")}>← Dashboard</button>
      </div>

      {/* HERO COVER */}
      <div className="vp-hero" style={{background: GRADIENTS[coverGradient]}}>
        <div className="vp-cover-overlay"/>
        <button className="vp-cover-edit" onClick={()=>document.getElementById("coverPicker").scrollIntoView({behavior:"smooth"})}>
          🎨 Change Cover
        </button>
      </div>

      {/* AVATAR + IDENTITY */}
      <div className="vp-avatar-area">
        <div className="vp-avatar-wrap">
          <div className="vp-avatar" style={{background: user.avatar?.url ? "transparent" : avatarColors[0], color: avatarColors[1]}}>
            {user.avatar?.url ? (
              <img src={user.avatar.url} alt={user.name} className="vp-avatar-img"/>
            ) : initials}
            {avatarUploading && <div className="vp-avatar-uploading">⏳</div>}
          </div>
          <div className="vp-avatar-edit-btn" onClick={()=>avatarInputRef.current?.click()} title="Change photo">
            📷
          </div>
          <input ref={avatarInputRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleAvatarPick}/>
        </div>
        <div className="vp-identity">
          <div className="vp-identity-name">{user.name}</div>
          <div className="vp-identity-email">{user.email}</div>
          <div className="vp-identity-badges">
            <span className="vp-role-badge">{user.role}</span>
            <span className="vp-member-badge">🗓️ {daysOnPlatform} days on VaultSphere</span>
          </div>
        </div>
      </div>

      {/* BODY GRID */}
      <div className="vp-body">

        {/* LEFT COLUMN */}
        <div>

          {/* EDIT PROFILE */}
          <div className="vp-card">
            <div className="vp-card-title">✏️ Edit Profile<div className="vp-card-line"/></div>

            <label className="vp-label">Display Name</label>
            <input className="vp-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name"/>

            <label className="vp-label">Email (cannot be changed)</label>
            <input className="vp-input" value={user.email} disabled/>

            <label className="vp-label">Bio</label>
            <textarea className="vp-textarea" value={bio} onChange={e=>setBio(e.target.value.slice(0,200))} placeholder="Tell people a bit about yourself…" rows={3}/>
            <div className="vp-char-count">{bio.length}/200</div>

            <label className="vp-label">🐙 GitHub</label>
            <div className="vp-input-icon-row">
              <span className="vp-input-icon">github.com/</span>
              <input className="vp-input vp-input-with-icon" value={github} onChange={e=>setGithub(e.target.value)} placeholder="yourusername"/>
            </div>

            <label className="vp-label">💼 LinkedIn</label>
            <div className="vp-input-icon-row">
              <span className="vp-input-icon">linkedin.com/in/</span>
              <input className="vp-input" style={{paddingLeft:130}} value={linkedin} onChange={e=>setLinkedin(e.target.value)} placeholder="yourusername"/>
            </div>

            <label className="vp-label">🌐 Portfolio</label>
            <input className="vp-input" value={portfolio} onChange={e=>setPortfolio(e.target.value)} placeholder="https://yoursite.com"/>

            <div className="vp-btn-row">
              <button className="vp-btn-primary" onClick={saveProfile} disabled={profileLoading}>
                {profileLoading ? "Saving…" : "💾 Save Profile"}
              </button>
            </div>
          </div>

          {/* COVER PICKER */}
          <div className="vp-card" id="coverPicker">
            <div className="vp-card-title">🎨 Cover Banner<div className="vp-card-line"/></div>
            <p style={{fontSize:13,color:"var(--text-3)",marginBottom:14}}>Pick a gradient for your profile banner</p>
            <div className="vp-cover-grid">
              {Object.entries(GRADIENTS).map(([key, grad])=>(
                <div
                  key={key}
                  className={`vp-cover-swatch${coverGradient===key?" active":""}`}
                  style={{background:grad}}
                  onClick={()=>setCoverGradient(key)}
                >
                  <span className="vp-cover-swatch-label">{GRADIENT_NAMES[key]}</span>
                </div>
              ))}
            </div>
            <button className="vp-btn-primary" style={{marginTop:12}} onClick={saveProfile} disabled={profileLoading}>
              {profileLoading?"Saving…":"Apply Cover"}
            </button>
          </div>

          {/* PROFILE PICTURE */}
          <div className="vp-card">
            <div className="vp-card-title">📷 Profile Picture<div className="vp-card-line"/></div>
            <div style={{display:"flex",alignItems:"center",gap:18,marginBottom:16}}>
              <div style={{width:72,height:72,borderRadius:"50%",overflow:"hidden",background:avatarColors[0],display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontFamily:"var(--font-display)",fontSize:28,color:avatarColors[1],fontWeight:700,border:"2px solid var(--border)"}}>
                {user.avatar?.url ? <img src={user.avatar.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : initials}
              </div>
              <div>
                <div style={{fontSize:14,fontWeight:600,color:"var(--text)",marginBottom:4}}>
                  {user.avatar?.url ? "Custom photo" : "Letter avatar (auto-generated)"}
                </div>
                <div style={{fontSize:12,color:"var(--text-3)"}}>Supports JPG, PNG, WEBP · Max 5MB</div>
              </div>
            </div>
            <div className="vp-btn-row">
              <button className="vp-btn-primary" onClick={()=>avatarInputRef.current?.click()} disabled={avatarUploading}>
                {avatarUploading ? "⏳ Uploading…" : "📷 Upload Photo"}
              </button>
              {user.avatar?.url && (
                <button className="vp-btn-ghost" onClick={removeAvatar}>Remove Photo</button>
              )}
            </div>
          </div>

          {/* CHANGE PASSWORD */}
          <div className="vp-card">
            <div className="vp-card-title">🔐 Change Password<div className="vp-card-line"/></div>
            <label className="vp-label">Current Password</label>
            <input className="vp-input" type="password" placeholder="Enter current password" value={currentPassword} onChange={e=>setCurrentPw(e.target.value)}/>
            <label className="vp-label">New Password</label>
            <input className="vp-input" type="password" placeholder="Enter new password" value={newPassword} onChange={e=>setNewPw(e.target.value)}/>
            <label className="vp-label">Confirm New Password</label>
            <input className="vp-input" type="password" placeholder="Confirm new password" value={confirmPassword} onChange={e=>setConfirmPw(e.target.value)}/>
            <p className="vp-hint">8+ chars · Uppercase · Lowercase · Number · Special char (@$!%*?&)</p>
            <button className="vp-btn-primary" onClick={changePassword} disabled={pwLoading}>
              {pwLoading ? "Updating…" : "Update Password"}
            </button>
          </div>

          {/* SECURITY */}
          <div className="vp-card">
            <div className="vp-card-title">🛡️ Security<div className="vp-card-line"/></div>
            <div className="vp-security-row">
              <span className="vp-security-label">Member since</span>
              <span className="vp-security-value">{user.createdAt ? new Date(user.createdAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : "—"}</span>
            </div>
            <div className="vp-security-row">
              <span className="vp-security-label">Account status</span>
              <span style={{fontSize:13,color:"var(--success)",fontWeight:600}}>✅ Verified</span>
            </div>
            <div className="vp-security-row">
              <span className="vp-security-label">Role</span>
              <span className="vp-security-value" style={{textTransform:"capitalize"}}>{user.role}</span>
            </div>
            <div style={{marginTop:16}}>
              <button className="vp-btn-danger" onClick={logout}>🚪 Sign Out</button>
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN */}
        <div>

          {/* STATS */}
          {stats && (
            <div className="vp-card">
              <div className="vp-card-title">📊 Stats<div className="vp-card-line"/></div>
              <div className="vp-stats-grid">
                <div className="vp-stat">
                  <div className="vp-stat-num">{stats.totalProjects}</div>
                  <div className="vp-stat-label">Projects</div>
                </div>
                <div className="vp-stat">
                  <div className="vp-stat-num">{stats.projectsWithFiles}</div>
                  <div className="vp-stat-label">Files Uploaded</div>
                </div>
                <div className="vp-stat">
                  <div className="vp-stat-num">{stats.daysOnPlatform}</div>
                  <div className="vp-stat-label">Days Active</div>
                </div>
                <div className="vp-stat">
                  <div className="vp-stat-num">{(stats.badges||[]).length}</div>
                  <div className="vp-stat-label">Badges Earned</div>
                </div>
              </div>
            </div>
          )}

          {/* BADGES */}
          <div className="vp-card">
            <div className="vp-card-title">🏆 Badges<div className="vp-card-line"/></div>
            <div className="vp-badges-grid">
              {ALL_BADGES.map(b=>(
                <div key={b.id} className={`vp-badge${earnedIds.has(b.id)?"":" vp-badge-locked"}`}>
                  <span className="vp-badge-icon">{b.icon}</span>
                  <div>
                    <div className="vp-badge-name">{b.label}</div>
                    <div className="vp-badge-desc">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ACTIVITY */}
          {stats?.activity?.length > 0 && (
            <div className="vp-card">
              <div className="vp-card-title">⚡ Recent Activity<div className="vp-card-line"/></div>
              {stats.activity.map((a,i)=>(
                <div key={i} className="vp-activity-item">
                  <div className={`vp-activity-dot ${a.type}`}/>
                  <div className="vp-activity-title">
                    <span style={{fontSize:11,fontWeight:700,color:a.type==="created"?"var(--success)":a.type==="updated"?"var(--accent)":"var(--danger)",marginRight:4}}>
                      {a.type==="created"?"Created":a.type==="updated"?"Updated":"Deleted"}
                    </span>
                    {a.title}
                  </div>
                  <div className="vp-activity-date">{new Date(a.date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
                </div>
              ))}
            </div>
          )}

          {/* LINKS PREVIEW */}
          {(github || linkedin || portfolio) && (
            <div className="vp-card">
              <div className="vp-card-title">🔗 Links<div className="vp-card-line"/></div>
              {github    && <a href={`https://github.com/${github}`}    target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid var(--border)",color:"var(--text)",textDecoration:"none",fontSize:14,fontWeight:500}}><span style={{fontSize:18}}>🐙</span>github.com/{github}</a>}
              {linkedin  && <a href={`https://linkedin.com/in/${linkedin}`} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:"1px solid var(--border)",color:"var(--text)",textDecoration:"none",fontSize:14,fontWeight:500}}><span style={{fontSize:18}}>💼</span>linkedin.com/in/{linkedin}</a>}
              {portfolio && <a href={portfolio} target="_blank" rel="noreferrer" style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",color:"var(--text)",textDecoration:"none",fontSize:14,fontWeight:500}}><span style={{fontSize:18}}>🌐</span>{portfolio.replace(/^https?:\/\//,"")}</a>}
            </div>
          )}

        </div>
      </div>

      {/* CROP / CONFIRM MODAL */}
      {showCropModal && (
        <div className="vp-modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowCropModal(false)}>
          <div className="vp-modal">
            <div className="vp-modal-title">Preview Photo</div>
            <div className="vp-crop-preview">
              {previewSrc && <img src={previewSrc} alt="preview"/>}
            </div>
            <p style={{fontSize:13,color:"var(--text-3)",textAlign:"center",marginBottom:4}}>
              This is how your profile picture will look
            </p>
            <div className="vp-modal-btn-row">
              <button className="vp-btn-primary" style={{flex:1}} onClick={confirmAvatarUpload}>
                ✅ Use This Photo
              </button>
              <button className="vp-btn-ghost" onClick={()=>{setShowCropModal(false);setPendingFile(null);}}>
                Cancel
              </button>
            </div>
            <div style={{textAlign:"center",marginTop:12}}>
              <button style={{background:"none",border:"none",color:"var(--text-3)",fontSize:12,cursor:"pointer"}} onClick={()=>{setShowCropModal(false);avatarInputRef.current?.click();}}>
                Choose a different photo
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Profile;